import React, { useState, useRef, useEffect } from "react";
import API_BASE_URL from "./config/api";
import questionmark from "./assests/questionmark.png";
// flight image removed for cleaner UI
import tripproLogo from "./assests/tripproLogo.png";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import styled, { keyframes, css } from "styled-components";

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUpStart = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// --- Styled Components ---

const StyledOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 0.3s ease-out;
  padding: 20px;
`;

const StyledModal = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  border-radius: 32px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.4);
  animation: ${scaleIn} 0.5s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 768px) {
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 25px;
  background: transparent;
  border: none;
  font-size: 32px;
  color: #004e61;
  cursor: pointer;
  z-index: 10;
  line-height: 1;
  transition: transform 0.2s ease;

  &:hover {
    transform: rotate(90deg);
    color: #1CA8CB;
  }
`;

const Header = styled.div`
  padding: 2rem 2rem 1rem;
  text-align: center;
  border-bottom: 1px solid rgba(0, 78, 97, 0.05);
`;

const Logo = styled.img`
  height: 80px;
  object-fit: contain;

  @media (max-width: 768px) {
    height: 60px;
  }
`;

const StepperContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 40px;
  margin-top: 1.5rem;
  width: 100%;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const StepIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  position: relative;
  opacity: ${props => props.$active ? 1 : props.$completed ? 0.8 : 0.4};
  transition: all 0.3s ease;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 14px;
    left: calc(50% + 20px);
    width: calc(100% - 40px);
    height: 2px;
    background: ${props => props.$completed ? '#1CA8CB' : '#e2e8f0'};
    z-index: 0;
  }
`;

const StepCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.$active || props.$completed ? '#1CA8CB' : 'white'};
  border: 2px solid ${props => props.$active || props.$completed ? '#1CA8CB' : '#e2e8f0'};
  color: ${props => props.$active || props.$completed ? 'white' : '#64748b'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  z-index: 1;
  transition: all 0.3s ease;
  box-shadow: ${props => props.$active ? '0 0 0 4px rgba(28, 168, 203, 0.2)' : 'none'};
`;

const StepLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${props => props.$active ? '#0f172a' : '#64748b'};
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const StepContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${slideUpStart} 0.4s ease-out;

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 78, 97, 0.1);
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

// StepIcon removed for cleaner UI

const Heading = styled.h2`
  font-family: "Merriweather", serif;
  font-size: 36px;
  color: #004e61;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 700;
  letter-spacing: -0.5px;
  
  span {
    color: #1CA8CB;
  }

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Description = styled.p`
  font-family: "Poppins", sans-serif;
  color: #555;
  text-align: center;
  margin-bottom: 2.5rem;
  max-width: 600px;
  font-size: 18px;
  line-height: 1.6;
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 78, 97, 0.1);
  border-left-color: #1CA8CB;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 2rem;
`;

// --- Form Elements ---

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: auto; /* Push to bottom */
  padding-top: 2rem;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #1CA8CB 0%, #008ba3 100%);
  color: white;
  border: none;
  padding: 14px 36px;
  border-radius: 50px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 20px -4px rgba(28, 168, 203, 0.4);
  margin-left: auto;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px -6px rgba(28, 168, 203, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #e0e0e0;
    color: #999;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #004e61;
  border: 2px solid rgba(0, 78, 97, 0.1);
  padding: 12px 24px;
  border-radius: 12px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #004e61;
    background: rgba(0, 78, 97, 0.05);
  }
`;

const GridContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  width: 100%;
  margin-bottom: 2rem;
`;

const SelectionButton = styled.button`
  padding: 10px 22px;
  border-radius: 50px;
  border: 1px solid ${props => props.$selected ? "transparent" : "#e0e0e0"};
  background: ${props => props.$selected ? "#1CA8CB" : "#ffffff"};
  color: ${props => props.$selected ? "white" : "#444"};
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.$selected ? "0 4px 14px rgba(28, 168, 203, 0.3)" : "none"};

  &:hover {
    transform: translateY(-2px);
    border-color: ${props => !props.$selected && "#1CA8CB"};
    box-shadow: ${props => props.$selected ? "0 6px 18px rgba(28, 168, 203, 0.4)" : "0 4px 12px rgba(0,0,0,0.08)"};
  }
`;

const GroupContainer = styled.div`
  background: #f8fbfc;
  border: 1px solid rgba(0, 78, 97, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  width: 90%;
  overflow-x: hidden;
`;

const GroupHeader = styled.h4`
  color: #004e61;
  margin-bottom: 1rem;
  font-family: "Merriweather", serif;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 17px;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  color: #555;
  font-size: 14px;
  font-family: "Poppins", sans-serif;
  margin-bottom: 0.8rem;
  font-weight: 500;
  width: 100%;

  input {
    margin-top: 8px;
    padding: 12px;
    border: 1px solid rgba(0, 78, 97, 0.2);
    border-radius: 8px;
    font-size: 16px;
    outline: none;
    transition: border 0.3s;

    &:focus {
      border-color: #1CA8CB;
      background: white;
    }
  }
`;

const ErrorBanner = styled.div`
  background: #ffe6e6;
  color: #d32f2f;
  border: 1px solid #ff9999;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  width: 100%;
  text-align: center;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
`;

const ReviewItem = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  font-family: "Poppins", sans-serif;
  font-size: 15px;

  strong {
    color: #004e61;
  }
  span {
    color: #666;
  }
`;

const DestinationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
  width: 100%;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
`;

const DestinationCard = styled.div`
  background: ${props => props.$selected ? "rgba(233, 246, 249, 0.8)" : "rgba(255, 255, 255, 0.5)"};
  backdrop-filter: blur(5px);
  border: 2px solid ${props => props.$selected ? "#1CA8CB" : "rgba(240, 240, 240, 0.5)"};
  border-radius: 20px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  transform: ${props => props.$selected ? "scale(1.02)" : "scale(1)"};

  &:hover {
    transform: translateY(-4px) ${props => props.$selected ? "scale(1.02)" : "scale(1.01)"};
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    border-color: #1CA8CB;
  }

  h4 {
    margin: 0;
    color: #004e61;
    font-size: 16px;
    font-family: "Merriweather", serif;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 12px;
    font-family: "Poppins", sans-serif;
    line-height: 1.4;
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 22px;
  height: 22px;
  background: #1CA8CB;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 4px 8px rgba(28, 168, 203, 0.3);
  animation: ${scaleIn} 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
`;

const RecommendedRoute = styled.div`
  background: #fdf6e9;
  border: 1px dashed #f39c12;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  h5 {
    margin: 0;
    color: #d35400;
    font-size: 14px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #7f8c8d;
    font-size: 13px;
  }
`;

const RouteButton = styled.button`
  background: #f39c12;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 50px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #e67e22;
    transform: scale(1.05);
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  background: #f0f0f0;
  border-radius: 50px;
  padding: 4px;
  margin-bottom: 24px;
  width: 300px;
`;

const ToggleButton = styled.button`
  flex: 1;
  border: none;
  background: ${props => props.$active ? "white" : "transparent"};
  color: ${props => props.$active ? "#004e61" : "#888"};
  padding: 10px;
  border-radius: 50px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${props => props.$active ? "0 2px 8px rgba(0,0,0,0.1)" : "none"};
  transition: all 0.2s ease;
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  width: 100%;
  margin-bottom: 24px;
`;

const Chip = styled.div`
  background: ${props => props.$selected ? "#1CA8CB" : "#fff"};
  color: ${props => props.$selected ? "white" : "#666"};
  border: 1px solid ${props => props.$selected ? "#1CA8CB" : "#ddd"};
  padding: 8px 20px;
  border-radius: 50px;
  cursor: pointer;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1CA8CB;
  }
`;

const TripTypeCard = styled.div`
  flex: 1;
  min-width: 150px;
  background: ${props => props.$selected ? "#e9f6f9" : "#fff"};
  border: 2px solid ${props => props.$selected ? "#1CA8CB" : "#f0f0f0"};
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:hover {
    transform: translateY(-4px);
    border-color: #1CA8CB;
  }

  .icon {
    font-size: 32px;
    margin-bottom: 8px;
  }

  h4 {
    margin: 0;
    color: #004e61;
    font-size: 18px;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 13px;
  }
`;

// --- Limit Exceeded Popup ---
const LimitPopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(6px);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 0.3s ease-out;
`;

const LimitPopupCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 40px;
  max-width: 440px;
  width: 90%;
  text-align: center;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  animation: ${scaleIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  .popup-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  h3 {
    font-family: "Merriweather", serif;
    color: #004e61;
    font-size: 22px;
    margin-bottom: 12px;
    font-weight: 700;
  }

  p {
    font-family: "Poppins", sans-serif;
    color: #555;
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 24px;
  }
`;

const LimitPopupActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ContactUsButton = styled.a`
  background: linear-gradient(135deg, #1CA8CB 0%, #008ba3 100%);
  color: white;
  border: none;
  padding: 12px 28px;
  border-radius: 50px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: all 0.3s ease;
  box-shadow: 0 6px 16px rgba(28, 168, 203, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(28, 168, 203, 0.4);
    color: white;
  }
`;

const DismissButton = styled.button`
  background: transparent;
  color: #666;
  border: 2px solid #e0e0e0;
  padding: 12px 28px;
  border-radius: 50px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #004e61;
    color: #004e61;
  }
`;

const modalStyles = {
  stateImage: {
    width: "100%",
    height: "200px",
    objectFit: "cover",
    borderRadius: "16px",
    marginTop: "20px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
  },
};

// React Select Styles
const selectStyles = {
  container: (base) => ({
    ...base,
    width: "100%",
    marginBottom: "1rem",
  }),
  control: (base, state) => ({
    ...base,
    borderRadius: "12px",
    padding: "6px",
    border: state.isFocused ? "1px solid #1CA8CB" : "1px solid rgba(0, 78, 97, 0.2)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(28, 168, 203, 0.1)" : "none",
    "&:hover": { borderColor: "#1CA8CB" },
    backgroundColor: "#fff",
    fontFamily: '"Poppins", sans-serif',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    zIndex: 105, // Higher than modal content
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#1ca8cb" : state.isFocused ? "#e9f6f9" : null,
    color: state.isSelected ? "white" : "#333",
    fontFamily: '"Poppins", sans-serif',
  })
};


const Modal = ({ closeModal, onStepChange }) => {
  const modalRef = useRef();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState({ value: "Kerala", label: "Kerala" }); // Default to Kerala
  const [stateImage, setStateImage] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [totalDays, setTotalDays] = useState(5);
  const [isFlexible, setIsFlexible] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [flexibleDuration, setFlexibleDuration] = useState("5–6 days");
  const [tripType, setTripType] = useState("Comfort");
  const [activities, setActivities] = useState([]);
  const [arrivalActivities, setArrivalActivities] = useState([]);
  const [departureActivities, setDepartureActivities] = useState([]);
  const [generalActivities, setGeneralActivities] = useState([]);
  const [selectedActivitiesByDestination, setSelectedActivitiesByDestination] = useState([]);
  const [hotelRating, setHotelRating] = useState(["4 Star"]);
  const [amenities, setAmenities] = useState(["Free Wi-Fi", "Swimming Pool", "Restaurant", "Parking"]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [roomCategories] = useState(["Standard room", "Deluxe room", "Suite Room", "Family room"]);
  const [selectedMealPlan, setSelectedMealPlan] = useState("CPAI");
  const [selectedRoomCategory, setSelectedRoomCategory] = useState("Standard room");
  const [guestGroups, setGuestGroups] = useState([{ adults: 2, children: [] }]);
  const [cabTypes, setCabTypes] = useState([]);
  const [selectedCabType, setSelectedCabType] = useState(null);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [selectedDestinations, setSelectedDestinations] = useState([]);

  // Booking Limit Popup State
  const [showLimitPopup, setShowLimitPopup] = useState(null); // null | 'duration' | 'adults'

  const MAX_DAYS = 12;
  const MAX_ADULTS = 8;

  // Couple Preference State
  const [intent, setIntent] = useState(null);
  const [stayPriority, setStayPriority] = useState(null);
  const [tripPace, setTripPace] = useState(null);
  const [goal, setGoal] = useState(null);
  const [prefSubStep, setPrefSubStep] = useState(0);

  const keralaDestinations = [
    { name: "Munnar", description: "Tea hills & waterfalls", icon: "🏔️" },
    { name: "Alleppey", description: "Houseboat & backwaters", icon: "🛶" },
    { name: "Kochi", description: "Heritage city", icon: "🏛️" },
    { name: "Thekkady", description: "Wildlife & spice", icon: "🐘" },
    { name: "Varkala", description: "Beach & cliff", icon: "🏖️" },
    { name: "Kovalam", description: "Lighthouse & beaches", icon: "🗼" },
    { name: "Vagamon", description: "Pine forests & meadows", icon: "🌲" },
    { name: "Wayanad", description: "Plantations & caves", icon: "🌿" },
  ];

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const currentMonthIndex = new Date().getMonth();
  const nextMonths = Array.from({ length: 9 }, (_, i) => months[(currentMonthIndex + i) % 12]);

  // Loading State
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Initiating AI Trip Planner...");

  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  const funFacts = [
    "India has the highest cricket ground in the world.",
    "The Kumbh Mela is visible from space.",
    "India is the wettest inhabited place on Earth.",
    "Rajasthan has a temple of rats.",
    "The value of Pi was first calculated in India.",
    "Yoga originated in India over 5,000 years ago.",
    "India produces the most spices in the world.",
    "The Taj Mahal changes color depending on the time of day.",
    "Varanasi is one of the oldest continuously inhabited cities in existence.",
    "India has a floating post office in Dal Lake, Srinagar."
  ];

  useEffect(() => {
    let interval;
    if (isLoading) {
      interval = setInterval(() => {
        setCurrentFactIndex((prev) => (prev + 1) % funFacts.length);
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  // Notify parent of step changes for tour synchronization
  useEffect(() => {
    if (onStepChange) {
      onStepChange(step);
    }
  }, [step, onStepChange]);

  const numberOfAdults = guestGroups.reduce((sum, group) => sum + group.adults, 0);
  const numberOfChildren = guestGroups.reduce((sum, group) => sum + group.children.length, 0);
  const childrenAges = guestGroups.flatMap((group) => group.children);

  // Intelligent Cab Auto-Selection based on capacity
  useEffect(() => {
    const totalAdults = guestGroups.reduce((sum, g) => sum + parseInt(g.adults || 0), 0);
    const totalChildren = guestGroups.reduce((sum, g) => sum + (g.children?.length || 0), 0);
    const totalPax = totalAdults + totalChildren;

    // Rule: > 3 adults OR > 4 total pax -> Requires SUV/MPV
    if (totalAdults > 3 || totalPax > 4) {
      const isSmallCab = !selectedCabType || ['Sedan', 'Hatchback'].some(t => selectedCabType.includes(t));
      if (isSmallCab) {
        if (cabTypes.find(t => t.includes('SUV'))) setSelectedCabType('SUV');
        else if (cabTypes.find(t => t.includes('MPV'))) setSelectedCabType('MPV');
      }
    }

    // Rule: Large groups > 7 pax -> Requires Tempo Traveller/Bus
    if (totalPax > 7) {
      const isMediumCab = !selectedCabType || ['SUV', 'MPV', 'Sedan', 'Hatchback'].some(t => selectedCabType.includes(t));
      if (isMediumCab) {
        // Try to find large vehicle types
        const largeType = cabTypes.find(t => t.includes('Tempo') || t.includes('Traveller') || t.includes('Bus'));
        if (largeType) setSelectedCabType(largeType);
      }
    }
  }, [guestGroups, cabTypes, selectedCabType]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/states`);
        const result = await response.json();
        setStates(result.map((s) => ({ value: s.state, label: s.state, stateImage: s.stateImage })));
      } catch (error) {
        console.error("Error fetching states:", error);
        setError("Failed to load states.");
      }
    };
    const fetchCabTypes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/cabs`);
        const result = await response.json();
        setCabTypes([...new Set(result.map((cab) => cab.type))]);
      } catch (error) {
        console.error("Error fetching cab types:", error);
      }
    };
    fetchStates();
    fetchCabTypes();
  }, []);

  useEffect(() => {
    const fetchDestinations = async () => {
      if (!selectedState) return;
      try {
        const response = await fetch(`${API_BASE_URL}/destinations/state/${selectedState.value}`);
        const result = await response.json();
        if (Array.isArray(result)) {
          // Filter to only show active destinations
          const activeDestinations = result.filter(dest => dest.isActive !== false);
          setDestinations(activeDestinations.map((dest) => ({ value: dest._id, label: dest.name, description: dest.description, images: dest.images })));
        } else {
          setDestinations([]);
          setError("No destinations found for this state");
        }
      } catch (error) {
        setError(error.message);
      }
    };
    fetchDestinations();
  }, [selectedState]);

  useEffect(() => {
    const fetchActivitiesForDestinations = async () => {
      if (!selectedDestinations.length) return;
      try {
        setArrivalActivities([]);
        setDepartureActivities([]);
        setGeneralActivities([]);
        setActivities([]);
        for (const destination of selectedDestinations) {
          const response = await fetch(`${API_BASE_URL}/activities/destination/${destination.value}`);
          const result = await response.json();
          if (Array.isArray(result)) {
            result.forEach((activity) => {
              const processedActivity = { id: activity._id, name: activity.name, description: activity.description, image: activity.image, rates: activity.rates, type: activity.type || "General", destination: destination.label, destinationId: destination.value };
              switch (activity.type) {
                case "Arrival": setArrivalActivities((prev) => [...prev, processedActivity]); break;
                case "Departure": setDepartureActivities((prev) => [...prev, processedActivity]); break;
                default: setGeneralActivities((prev) => [...prev, processedActivity]); break;
              }
              setActivities((prev) => [...prev, processedActivity]);
            });
          }
        }
      } catch (error) {
        setError(error.message);
      }
    };
    fetchActivitiesForDestinations();
  }, [selectedDestinations]);

  const handleDestinationChange = (selectedOptions) => {
    const rawOptions = selectedOptions || [];
    const uniqueOptions = [];
    const seen = new Set();
    for (const opt of rawOptions) {
      const key = (opt.label || "").trim().toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        uniqueOptions.push(opt);
      }
    }
    setSelectedDestinations(uniqueOptions);
    setSelectedActivitiesByDestination(uniqueOptions.map((dest) => ({ destinationId: dest.value, arrivalActivity: null, generalActivities: [], departureActivity: null })));
  };

  const handleGenerateItinerary = async () => {
    setIsLoading(true);
    setLoadingMessage("Initiating AI Trip Planner...");
    const messages = ["Searching for best hotels...", "Checking cab availability...", "Curating activities...", "Finalizing itinerary..."];
    let msgIndex = 0;
    const messageInterval = setInterval(() => { if (msgIndex < messages.length) { setLoadingMessage(messages[msgIndex]); msgIndex++; } }, 2500);

    const formatDateToLocal = (date) => {
      if (!date) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // Track "Generate Itinerary" click
    import("react-ga4").then((ReactGA) => {
      ReactGA.default.event({
        category: "Planner",
        action: "Generate Itinerary",
        label: `Destination: ${selectedState?.label || "Unknown"}`,
      });
    });

    // Since activities are no longer manually selected, let AI determine activities based on destinations
    const generatedDescription = `Plan a ${totalDays}-day trip to ${selectedState?.label || "New Delhi"}.`;

    // Handle Flexible Dates logic for API
    let apiStartDate = startDate;
    let apiEndDate = endDate;
    let apiDuration = totalDays;

    if (isFlexible && selectedMonths.length > 0) {
      const year = new Date().getFullYear();
      const monthMap = { "Jan": 0, "Feb": 1, "Mar": 2, "Apr": 3, "May": 4, "Jun": 5, "Jul": 6, "Aug": 7, "Sep": 8, "Oct": 9, "Nov": 10, "Dec": 11 };
      const startMonth = monthMap[selectedMonths[0]];
      apiStartDate = new Date(year, startMonth, 1);
      
      const durMatch = flexibleDuration.match(/(\d+)/);
      apiDuration = durMatch ? parseInt(durMatch[0]) + 1 : 6; // e.g. "5-6 days" -> 6
      apiEndDate = new Date(apiStartDate);
      apiEndDate.setDate(apiStartDate.getDate() + apiDuration - 1);
    }

    const requestBody = {
      stateName: selectedState?.label || "Kerala",
      destinations: selectedDestinations.map((d) => d.label),
      startDate: formatDateToLocal(apiStartDate),
      endDate: formatDateToLocal(apiEndDate),
      duration: apiDuration,
      activities: [], 
      itinerarySequence: [], 
      hotelRating: hotelRating.map((r) => parseInt(r.split(" ")[0])),
      amenities: selectedAmenities.length ? selectedAmenities : ["Free Wi-Fi", "Parking"],
      roomCategory: selectedRoomCategory || (tripType === "Luxury" ? "Suite Room" : "Standard room"),
      cabType: selectedCabType || (tripType === "Budget" ? "Hatchback" : tripType === "Luxury" ? "SUV" : "Sedan"),
      description: generatedDescription,
      coverImage: (selectedDestinations.length > 0 && selectedDestinations[0].images?.length > 0) 
        ? selectedDestinations[0].images[0] 
        : (stateImage || ""),
      mealPlan: selectedMealPlan || (tripType === "Luxury" ? "MAPAI" : "CPAI"),
      guests: guestGroups.map((group) => ({ 
        adults: parseInt(group.adults, 10), 
        children: group.children.map((age) => parseInt(age, 10)).filter((age) => !isNaN(age)) 
      })),
      intent,
      stay_priority: stayPriority,
      pace: tripPace,
      goal,
      tripType,
    };

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    try {
      const response = await fetch(`${API_BASE_URL}/ai-itinerary/generate`, { method: "POST", headers: myHeaders, body: JSON.stringify(requestBody), redirect: "follow" });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to generate itinerary");
      setResponse(result);
      clearInterval(messageInterval);
      setIsLoading(false);
      navigate("/aitripdetail", { state: { itinerary: result } });
    } catch (error) {
      clearInterval(messageInterval);
      setIsLoading(false);
      setError(error.message || "Failed");
    }
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return;
    const diffTime = end - start;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const days = Math.max(1, diffDays + 1);
    setTotalDays(days);
    if (days > MAX_DAYS) {
      setShowLimitPopup('duration');
    }
  };

  const nextStep = () => step < 4 && setStep(step + 1);
  const prevStep = () => step > 1 && setStep(step - 1);
  const handleOverlayClick = (e) => modalRef.current && !modalRef.current.contains(e.target) && closeModal();
  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setDestinations([]); setSelectedDestinations([]); setActivities([]); setSelectedActivitiesByDestination([]); setStateImage(selectedOption?.stateImage || "");
  };

  // Step Indicators
  const renderProgress = () => (
    <StepperContainer>
      {[
        { label: "Locate", id: 1 },
        { label: "Date", id: 2 },
        { label: "Style & Prefs", id: 3 },
        { label: "Guests", id: 4 }
      ].map((s, idx, arr) => (
        <StepIndicator
          key={s.id}
          $active={step === s.id}
          $completed={step > s.id}
        >
          <StepCircle $active={step === s.id} $completed={step > s.id}>
            {step > s.id ? "✓" : s.id}
          </StepCircle>
          <StepLabel $active={step === s.id}>{s.label}</StepLabel>
        </StepIndicator>
      ))}
    </StepperContainer>
  );

  return (
    <StyledOverlay onClick={handleOverlayClick}>
      <StyledModal ref={modalRef}>
        <CloseButton onClick={closeModal}>×</CloseButton>
        {!isLoading && (
          <Header>
            <Logo src={tripproLogo} alt="Logo" />
            {renderProgress()}
          </Header>
        )}

        {error && <ErrorBanner><strong>Error:</strong><br />{error}</ErrorBanner>}

        {isLoading ? (
          <StepContent>
            <div style={{ textAlign: "center", padding: "40px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Spinner />
              <Heading>Generating Your Trip...</Heading>
              <Description>{loadingMessage}</Description>

              {/* Fun Facts Section */}
              <div style={{
                marginTop: "20px",
                padding: "20px",
                background: "#f0f9fa",
                borderRadius: "16px",
                maxWidth: "500px",
                width: "100%",
                animation: "fadeIn 0.5s ease-in-out"
              }}>
                <h4 style={{
                  fontFamily: '"Merriweather", serif',
                  color: "#004e61",
                  fontSize: "16px",
                  marginBottom: "8px"
                }}>Did You Know?</h4>
                <p style={{
                  fontFamily: '"Poppins", sans-serif',
                  color: "#555",
                  fontSize: "14px",
                  lineHeight: "1.5",
                  minHeight: "42px" // Prevent layout shift
                }}>
                  {funFacts[currentFactIndex] || "Traveling opens your heart, broadens your mind, and fills your life with stories to tell."}
                </p>
              </div>
            </div>
          </StepContent>
        ) : (
          <>
            {step === 1 && (
              <StepContent>
                <Heading>Where in Kerala would you like to go?</Heading>
                <Description>Select one or more destinations to start your journey.</Description>

                {/* Recommended Route Section */}
                <RecommendedRoute>
                  <div>
                    <h5>Not sure? Start with a popular route</h5>
                    <p>Kerala Honeymoon Route: Munnar → Alleppey → Kochi</p>
                  </div>
                  <RouteButton onClick={() => {
                    const munnar = destinations.find(d => d.label === "Munnar");
                    const alleppey = destinations.find(d => d.label === "Alleppey");
                    const kochi = destinations.find(d => d.label === "Kochi");
                    const selected = [munnar, alleppey, kochi].filter(Boolean);
                    setSelectedDestinations(selected);
                    setStep(2);
                  }}>
                    💕 Use Recommended Route
                  </RouteButton>
                </RecommendedRoute>

                <DestinationGrid>
                  {keralaDestinations.map((dest) => {
                    const isSelected = selectedDestinations.some(d => (d.label || "").trim().toLowerCase() === dest.name.trim().toLowerCase());
                    return (
                      <DestinationCard
                        key={dest.name}
                        $selected={isSelected}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedDestinations(prev => prev.filter(d => (d.label || "").trim().toLowerCase() !== dest.name.trim().toLowerCase()));
                          } else {
                            const destObj = destinations.find(d => (d.label || "").trim().toLowerCase() === dest.name.trim().toLowerCase());
                            if (!destObj) return;
                            setSelectedDestinations(prev => [...prev, destObj]);
                          }
                        }}
                      >
                        {isSelected && <SelectedBadge>✓</SelectedBadge>}
                        <div style={{ fontSize: '28px', marginBottom: '8px' }}>{dest.icon}</div>
                        <h4>{dest.name}</h4>
                        <p>{dest.description}</p>
                      </DestinationCard>
                    );
                  })}
                </DestinationGrid>

                <div style={{ width: "100%", marginTop: "auto" }}>
                  <Label>Or search for other destinations</Label>
                  <Select
                    isMulti
                    options={destinations}
                    value={selectedDestinations}
                    onChange={handleDestinationChange}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    isOptionSelected={(option, selectValue) => 
                      selectValue.some(v => (v.label || "").trim().toLowerCase() === (option.label || "").trim().toLowerCase())
                    }
                    placeholder="Search destinations..."
                    styles={selectStyles}
                  />
                </div>

                <ButtonContainer>
                  <PrimaryButton onClick={nextStep} disabled={selectedDestinations.length === 0}>Next</PrimaryButton>
                </ButtonContainer>
              </StepContent>
            )}

            {step === 2 && (
              <StepContent>
                <Heading>When are you planning to travel?</Heading>
                <Description>Choose between fixed dates or a flexible window.</Description>

                <ToggleContainer>
                  <ToggleButton $active={!isFlexible} onClick={() => setIsFlexible(false)}>Fixed Dates</ToggleButton>
                  <ToggleButton $active={isFlexible} onClick={() => setIsFlexible(true)}>Flexible Dates</ToggleButton>
                </ToggleContainer>

                {!isFlexible ? (
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1rem' }}>
                    <DatePicker
                      selected={startDate}
                      onChange={(dates) => {
                        const [start, end] = dates;
                        setStartDate(start);
                        setEndDate(end);
                        if (start && end) calculateDuration(start, end);
                      }}
                      startDate={startDate}
                      endDate={endDate}
                      selectsRange
                      minDate={new Date()}
                      maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                      inline
                    />
                    {startDate && endDate && (
                      <div style={{ marginTop: '16px', padding: '8px 20px', background: totalDays > MAX_DAYS ? '#fff3e0' : '#e9f6f9', borderRadius: '50px', color: totalDays > MAX_DAYS ? '#e65100' : '#004e61', fontWeight: 600, border: `1px solid ${totalDays > MAX_DAYS ? 'rgba(230, 81, 0, 0.3)' : 'rgba(28, 168, 203, 0.3)'}`, fontSize: '14px', animation: 'fadeIn 0.3s' }}>
                        {totalDays} {totalDays === 1 ? 'Day' : 'Days'} Selected {totalDays > MAX_DAYS && `(max ${MAX_DAYS} days)`}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ width: '100%', textAlign: 'center' }}>
                    <Label>Select Month(s)</Label>
                    <ChipContainer>
                      {nextMonths.map(month => (
                        <Chip
                          key={month}
                          $selected={selectedMonths.includes(month)}
                          onClick={() => setSelectedMonths(prev =>
                            prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month].slice(-2)
                          )}
                        >
                          {month}
                        </Chip>
                      ))}
                    </ChipContainer>

                    <Label>Select Duration (Mandatory)</Label>
                      <ChipContainer>
                        {["3-4 days", "5-6 days", "7-8 days", "9-12 days"].map(dur => (
                          <Chip
                            key={dur}
                            $selected={flexibleDuration === dur}
                            onClick={() => setFlexibleDuration(dur)}
                          >
                            {dur} {dur === "5-6 days" && <span style={{ fontSize: '10px', marginLeft: '4px', opacity: 0.8 }}>(Most Popular)</span>}
                          </Chip>
                        ))}
                      </ChipContainer>
                  </div>
                )}

                <ButtonContainer>
                  <SecondaryButton onClick={prevStep}>Back</SecondaryButton>
                  <PrimaryButton
                    onClick={() => {
                      if (!isFlexible && totalDays > MAX_DAYS) {
                        setShowLimitPopup('duration');
                        return;
                      }
                      nextStep();
                    }}
                    disabled={!isFlexible ? (!startDate || !endDate || totalDays > MAX_DAYS) : (selectedMonths.length === 0 || !flexibleDuration)}
                  >
                    Next
                  </PrimaryButton>
                </ButtonContainer>
              </StepContent>
            )}

            {step === 3 && (
              <StepContent>
                <Heading>
                  {prefSubStep === 0 && "What's your trip style?"}
                  {prefSubStep === 1 && "What kind of experience do you want?"}
                  {prefSubStep === 2 && "What matters more?"}
                  {prefSubStep === 3 && "How do you like your trip?"}
                  {prefSubStep === 4 && "What matters most for this trip?"}
                </Heading>
                <Description>
                  {prefSubStep === 0 && "This helps us select the right hotels and pace for you."}
                  {prefSubStep === 1 && "Select your core intent for the trip."}
                  {prefSubStep === 2 && "Tell us your stay priority."}
                  {prefSubStep === 3 && "Select your preferred trip pace."}
                  {prefSubStep === 4 && "Choose the main goal of your trip."}
                </Description>

                {/* Mini progress indicator for preferences */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', justifyContent: 'center' }}>
                  {[0, 1, 2, 3, 4].map(idx => (
                    <div 
                      key={idx} 
                      style={{
                        width: '30px', 
                        height: '4px', 
                        borderRadius: '2px', 
                        background: idx === prefSubStep ? '#1CA8CB' : idx < prefSubStep ? 'rgba(28, 168, 203, 0.4)' : '#e2e8f0',
                        transition: 'background 0.3s'
                      }} 
                    />
                  ))}
                </div>

                {prefSubStep === 0 && (
                  <div style={{ display: 'flex', gap: '20px', width: '100%', marginBottom: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {[
                      { type: "Budget", icon: "🎒", desc: "Value for money, 3-star hotels" },
                      { type: "Comfort", icon: "✨", desc: "Modern amenities, 4-star hotels" },
                      { type: "Luxury", icon: "👑", desc: "Premium experience, 5-star hotels" }
                    ].map((item) => (
                      <TripTypeCard
                        key={item.type}
                        $selected={tripType === item.type}
                        onClick={() => {
                          setTripType(item.type);
                          setHotelRating(item.type === "Budget" ? ["3 Star"] : item.type === "Comfort" ? ["4 Star"] : ["5 Star"]);
                          setTimeout(() => setPrefSubStep(1), 200);
                        }}
                      >
                        <div className="icon">{item.icon}</div>
                        <h4>{item.type}</h4>
                        <p>{item.desc}</p>
                      </TripTypeCard>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%', maxWidth: '400px', marginBottom: '24px' }}>
                  {prefSubStep === 1 && [
                    { label: "Relax & unwind", icon: "🌅" },
                    { label: "Explore places", icon: "🗺️" },
                    { label: "Mix of both", icon: "⚖️" }
                  ].map(opt => (
                    <SelectionButton 
                      key={opt.label} 
                      $selected={intent === opt.label}
                      onClick={() => { setIntent(opt.label); setTimeout(() => setPrefSubStep(2), 200); }}
                      style={{ padding: '16px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}
                    >
                      <span>{opt.icon}</span> {opt.label}
                    </SelectionButton>
                  ))}

                  {prefSubStep === 2 && [
                    { label: "Premium stays", icon: "🏨" },
                    { label: "Better locations", icon: "📍" },
                    { label: "Budget balance", icon: "💰" }
                  ].map(opt => (
                    <SelectionButton 
                      key={opt.label} 
                      $selected={stayPriority === opt.label}
                      onClick={() => { setStayPriority(opt.label); setTimeout(() => setPrefSubStep(3), 200); }}
                      style={{ padding: '16px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}
                    >
                      <span>{opt.icon}</span> {opt.label}
                    </SelectionButton>
                  ))}

                  {prefSubStep === 3 && [
                    { label: "Chill (slow & relaxed)", icon: "☕" },
                    { label: "Balanced (steady)", icon: "🚶" },
                    { label: "Packed (see it all)", icon: "🏃" }
                  ].map(opt => (
                    <SelectionButton 
                      key={opt.label} 
                      $selected={tripPace === opt.label}
                      onClick={() => { setTripPace(opt.label); setTimeout(() => setPrefSubStep(4), 200); }}
                      style={{ padding: '16px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}
                    >
                      <span>{opt.icon}</span> {opt.label}
                    </SelectionButton>
                  ))}

                  {prefSubStep === 4 && [
                    { label: "Peaceful time together", icon: "❤️" },
                    { label: "Memorable experiences", icon: "✨" },
                    { label: "Instagram-worthy moments", icon: "📸" }
                  ].map(opt => (
                    <SelectionButton 
                      key={opt.label} 
                      $selected={goal === opt.label}
                      onClick={() => { setGoal(opt.label); setTimeout(() => nextStep(), 200); }}
                      style={{ padding: '16px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}
                    >
                      <span>{opt.icon}</span> {opt.label}
                    </SelectionButton>
                  ))}
                </div>

                <ButtonContainer>
                  <SecondaryButton onClick={() => prefSubStep > 0 ? setPrefSubStep(prefSubStep - 1) : prevStep()}>Back</SecondaryButton>
                  {/* Next button only active when goal is selected and prefSubStep is 4 OR tripType selected and prefSubStep 0 */}
                  {prefSubStep === 0 && <PrimaryButton onClick={() => setPrefSubStep(1)} disabled={!tripType}>Next</PrimaryButton>}
                  {prefSubStep === 4 && <PrimaryButton onClick={nextStep} disabled={!goal}>Next</PrimaryButton>}
                </ButtonContainer>
              </StepContent>
            )}

            {step === 4 && (
              <StepContent>
                <Heading>Who's traveling?</Heading>
                <Description>Tell us about your group.</Description>

                <div style={{ width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div className="guest-control-modal" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fbfc', padding: '15px 20px', borderRadius: '16px', border: '1px solid rgba(0, 78, 97, 0.1)' }}>
                    <span style={{ fontWeight: 600, color: '#004e61' }}>Adults</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <button 
                        style={{ width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #1CA8CB', background: 'white', color: '#1CA8CB', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => {
                          const n = [...guestGroups];
                          n[0].adults = Math.max(1, (n[0].adults || 1) - 1);
                          setGuestGroups(n);
                        }}
                      >-</button>
                      <span style={{ fontSize: '18px', fontWeight: 700, minWidth: '20px', textAlign: 'center' }}>{guestGroups[0].adults}</span>
                      <button 
                        style={{ width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: guestGroups[0].adults >= MAX_ADULTS ? '#ccc' : '#1CA8CB', color: 'white', fontSize: '20px', cursor: guestGroups[0].adults >= MAX_ADULTS ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => {
                          if (guestGroups[0].adults >= MAX_ADULTS) {
                            setShowLimitPopup('adults');
                            return;
                          }
                          const n = [...guestGroups];
                          n[0].adults = (n[0].adults || 0) + 1;
                          setGuestGroups(n);
                        }}
                      >+</button>
                    </div>
                  </div>

                  <div className="guest-control-modal" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fbfc', padding: '15px 20px', borderRadius: '16px', border: '1px solid rgba(0, 78, 97, 0.1)' }}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#004e61', display: 'block' }}>Children</span>
                      <span style={{ fontSize: '11px', color: '#888' }}>Ages 0-12</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <button 
                        style={{ width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #1CA8CB', background: 'white', color: '#1CA8CB', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => {
                          const n = [...guestGroups];
                          if (n[0].children.length > 0) {
                            n[0].children.pop();
                            setGuestGroups(n);
                          }
                        }}
                      >-</button>
                      <span style={{ fontSize: '18px', fontWeight: 700, minWidth: '20px', textAlign: 'center' }}>{guestGroups[0].children.length}</span>
                      <button 
                        style={{ width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: '#1CA8CB', color: 'white', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => {
                          const n = [...guestGroups];
                          n[0].children.push(10); // Default age 10
                          setGuestGroups(n);
                        }}
                      >+</button>
                    </div>
                  </div>
                  
                  {guestGroups[0].children.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '10px' }}>
                      {guestGroups[0].children.map((age, cIdx) => (
                        <div key={cIdx}>
                          <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>Child {cIdx + 1} Age</label>
                          <input
                            type="number"
                            min="0"
                            max="12"
                            value={age}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(0, 78, 97, 0.2)', fontSize: '14px' }}
                            onChange={e => {
                              const n = [...guestGroups];
                              n[0].children[cIdx] = parseInt(e.target.value) || 0;
                              setGuestGroups(n);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <ButtonContainer>
                  <SecondaryButton onClick={prevStep}>Back</SecondaryButton>
                  <PrimaryButton onClick={handleGenerateItinerary}>Generate My Trip Plan</PrimaryButton>
                </ButtonContainer>
              </StepContent>
            )}
          </>
        )}

        {/* Limit Exceeded Popup */}
        {showLimitPopup && (
          <LimitPopupOverlay onClick={() => setShowLimitPopup(null)}>
            <LimitPopupCard onClick={(e) => e.stopPropagation()}>
              <div className="popup-icon">
                {showLimitPopup === 'duration' ? '📅' : '👥'}
              </div>
              <h3>
                {showLimitPopup === 'duration'
                  ? 'Trip Duration Limit'
                  : 'Group Size Limit'}
              </h3>
              <p>
                {showLimitPopup === 'duration'
                  ? `We currently support trips up to ${MAX_DAYS} days through our website. For longer trips, please contact us and our travel experts will help you plan the perfect itinerary!`
                  : `We currently support bookings for up to ${MAX_ADULTS} adults through our website. For larger groups, please contact us and our team will be happy to assist!`}
              </p>
              <LimitPopupActions>
                <ContactUsButton href="/contact">Contact Us</ContactUsButton>
                <DismissButton onClick={() => setShowLimitPopup(null)}>Got it</DismissButton>
              </LimitPopupActions>
            </LimitPopupCard>
          </LimitPopupOverlay>
        )}
      </StyledModal>
    </StyledOverlay>
  );
};

export default Modal;